import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Send, Loader2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_CATEGORIES = [
  { value: 'it', label: 'IT / Tehnologija' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'prodaja', label: 'Prodaja' },
  { value: 'proizvodnja', label: 'Proizvodnja' },
  { value: 'ugostiteljstvo', label: 'Ugostiteljstvo' },
  { value: 'trgovina', label: 'Trgovina' },
  { value: 'gradevina', label: 'Građevina' },
  { value: 'obrazovanje', label: 'Obrazovanje' },
  { value: 'zdravstvo', label: 'Zdravstvo' },
  { value: 'administracija', label: 'Administracija' },
  { value: 'ostalo', label: 'Ostalo' },
];

const EMPLOYMENT = [
  { value: 'full_time', label: 'Puno radno vrijeme' },
  { value: 'part_time', label: 'Nepuno radno vrijeme' },
  { value: 'contract', label: 'Ugovor / projekt' },
  { value: 'internship', label: 'Praksa' },
  { value: 'seasonal', label: 'Sezonski' },
];

export default function JobSubmitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [form, setForm] = useState({
    title: '', company: '', location: '', category: 'ostalo',
    employment_type: 'full_time', description: '', salary_range_eur: '',
    application_email: '', application_url: '', logo_data_url: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function onPickLogo(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Logo mora biti manji od 2MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(p => ({ ...p, logo_data_url: String(reader.result || '') }));
    reader.readAsDataURL(f);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Morate biti prijavljeni za predaju oglasa');
      loginWithRedirect();
      return;
    }
    if (!form.title.trim() || !form.company.trim() || !form.application_email.trim()) {
      toast.error('Molimo ispunite naslov, tvrtku i kontakt e-mail');
      return;
    }
    setSending(true);
    try {
      const token = await getAccessTokenSilently();
      const payload = {
        title: form.title.trim(),
        company: form.company.trim(),
        // Send logo data URL — backend persists this so listing shows the company logo.
        company_logo_url: form.logo_data_url || null,
        location: form.location.trim(),
        category: form.category,
        employment_type: form.employment_type,
        description: form.description,
        salary_range_eur: form.salary_range_eur,
        application_email: form.application_email.trim(),
        application_url: form.application_url.trim(),
      };
      const res = await fetch(`${API}/public/jobs/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSent(true);
      toast.success('Oglas za posao uspješno predan!');
    } catch (err) {
      toast.error('Greška pri slanju: ' + err.message);
    } finally { setSending(false); }
  }

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="job-submit-success">
        <div className="text-5xl mb-4">✅</div>
        <h1 className="text-2xl font-bold mb-3 text-gray-900 dark:text-gray-100">Hvala!</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-4">Vaš oglas za posao je zaprimljen i čeka pregled urednika. Bit će objavljen u najkraćem mogućem roku.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/posao" className="inline-block px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium">Natrag na oglase za posao</Link>
          <button onClick={() => navigate('/moji-oglasi')} className="inline-block px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm rounded-lg font-medium">Moji oglasi</button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="job-login-required">
        <div className="inline-flex items-center justify-center w-14 h-14 bg-red-600/10 text-red-600 rounded-full mb-3"><Briefcase size={26} /></div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Prijava potrebna</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-5">Za predaju oglasa za posao morate biti prijavljeni — tako oglas možete kasnije urediti ili obrisati iz vaše korisničke kontrole.</p>
        <button onClick={() => loginWithRedirect()} className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg font-medium" data-testid="job-login-btn">Prijavi se</button>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Predaj oglas za posao — e-mina.eu</title></Helmet>
      <div className="max-w-2xl mx-auto px-4 py-10" data-testid="job-submit-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-red-600/10 text-red-600 rounded-full mb-3"><Briefcase size={26} /></div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Predaj oglas za posao</h1>
          <p className="text-gray-500 dark:text-gray-400">Objavi poziciju na e-mina.eu Posao — brzo, ciljano, s lokalnom publikom.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Naslov pozicije *</span>
              <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400"
                placeholder="npr. Frontend developer (React)" data-testid="job-title" required />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tvrtka *</span>
              <input type="text" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400"
                placeholder="Ime tvrtke" data-testid="job-company" required />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lokacija</span>
              <input type="text" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="npr. Čakovec ili Remote" data-testid="job-location" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kategorija</span>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:bg-[#0f172a] dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none" data-testid="job-category">
                {JOB_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tip zaposlenja</span>
              <select value={form.employment_type} onChange={e => setForm(p => ({ ...p, employment_type: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:bg-[#0f172a] dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none" data-testid="job-employment">
                {EMPLOYMENT.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plaća (EUR, opcionalno)</span>
              <input type="text" value={form.salary_range_eur} onChange={e => setForm(p => ({ ...p, salary_range_eur: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="npr. 1200 – 1500" data-testid="job-salary" />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opis pozicije</span>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={8} className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400 resize-y"
                placeholder="Opišite radno mjesto, odgovornosti, kvalifikacije, benefite…" data-testid="job-description" />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kontakt e-mail *</span>
              <input type="email" value={form.application_email} onChange={e => setForm(p => ({ ...p, application_email: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-red-400"
                placeholder="posao@tvrtka.hr" data-testid="job-email" required />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL za prijavu (opcionalno)</span>
              <input type="url" value={form.application_url} onChange={e => setForm(p => ({ ...p, application_url: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-700 bg-transparent dark:text-gray-100 rounded-lg px-4 py-2.5 text-sm outline-none"
                placeholder="https://…" data-testid="job-apply-url" />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Logo tvrtke (opcionalno, max 2MB)</span>
              <input type="file" accept="image/*" onChange={onPickLogo} className="text-sm dark:text-gray-300" data-testid="job-logo" />
              {form.logo_data_url && <img src={form.logo_data_url} alt="logo preview" className="mt-2 h-14 rounded" />}
            </label>
          </div>
          <button type="submit" disabled={sending}
            className="w-full flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            data-testid="job-submit-btn">
            {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            {sending ? 'Šaljem…' : 'Predaj oglas'}
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center" data-testid="job-rules-note">
            Urednik zadržava pravo uklanjanja ili pauziranja oglasa ukoliko nije s pravilima i standardima portala.
          </p>
        </form>
      </div>
    </>
  );
}
